import { AlertTriangle, CheckCircle, Clock, XCircle, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ContractStatusBannerProps {
  contractStatus: string;
  kycOriginStatus: string;
  kycDestinationStatus: string;
  className?: string;
}

interface BlockedOperation {
  operation: string;
  reason: string;
}

/**
 * Banner que exibe o status do contrato da conta virtual para o correntista.
 * Mostra alertas quando há bloqueios de operações.
 */
export function ContractStatusBanner({
  contractStatus,
  kycOriginStatus,
  kycDestinationStatus,
  className,
}: ContractStatusBannerProps) {
  // Determinar operações bloqueadas
  const blockedOperations: BlockedOperation[] = [];

  // Verificar status do contrato
  if (contractStatus === 'PENDING_APPROVAL') {
    blockedOperations.push({
      operation: 'Todas as operações',
      reason: 'Contrato aguardando aprovação',
    });
  } else if (contractStatus === 'SUSPENDED') {
    blockedOperations.push({
      operation: 'Todas as operações',
      reason: 'Contrato suspenso',
    });
  } else if (contractStatus === 'TERMINATED') {
    blockedOperations.push({
      operation: 'Todas as operações',
      reason: 'Contrato encerrado',
    });
  }

  // Verificar KYC de origem (recebimentos)
  if (contractStatus === 'ACTIVE') {
    if (kycOriginStatus === 'PENDING') {
      blockedOperations.push({
        operation: 'Recebimentos (Pix, transferências)',
        reason: 'KYC de origem pendente de verificação',
      });
    } else if (kycOriginStatus === 'REJECTED') {
      blockedOperations.push({
        operation: 'Recebimentos (Pix, transferências)',
        reason: 'KYC de origem rejeitado',
      });
    }

    // Verificar KYC de destino (envios)
    if (kycDestinationStatus === 'PENDING') {
      blockedOperations.push({
        operation: 'Envios (Pix, saques, transferências)',
        reason: 'KYC de destino pendente de verificação',
      });
    } else if (kycDestinationStatus === 'REJECTED') {
      blockedOperations.push({
        operation: 'Envios (Pix, saques, transferências)',
        reason: 'KYC de destino rejeitado',
      });
    }
  }

  // Se não há bloqueios, não exibir o banner
  if (blockedOperations.length === 0) {
    return null;
  }

  // Determinar severidade do banner
  const isFullyBlocked = contractStatus !== 'ACTIVE';
  const severity = isFullyBlocked ? 'error' : 'warning';

  const severityStyles = {
    error: {
      bg: 'bg-red-500/10 border-red-500/30',
      icon: 'text-red-400',
      title: 'text-red-300',
      text: 'text-red-200/80',
    },
    warning: {
      bg: 'bg-yellow-500/10 border-yellow-500/30',
      icon: 'text-yellow-400',
      title: 'text-yellow-300',
      text: 'text-yellow-200/80',
    },
  };

  const styles = severityStyles[severity];

  const getStatusIcon = () => {
    switch (contractStatus) {
      case 'PENDING_APPROVAL':
        return <Clock className={cn('w-6 h-6', styles.icon)} />;
      case 'SUSPENDED':
        return <AlertTriangle className={cn('w-6 h-6', styles.icon)} />;
      case 'TERMINATED':
        return <XCircle className={cn('w-6 h-6', styles.icon)} />;
      default:
        return <Info className={cn('w-6 h-6', styles.icon)} />;
    }
  };

  const getStatusTitle = () => {
    switch (contractStatus) {
      case 'PENDING_APPROVAL':
        return 'Conta em Análise';
      case 'SUSPENDED':
        return 'Conta Suspensa';
      case 'TERMINATED':
        return 'Conta Encerrada';
      default:
        return 'Atenção: Operações Restritas';
    }
  };

  const getInstructions = () => {
    switch (contractStatus) {
      case 'PENDING_APPROVAL':
        return 'Sua conta está sendo analisada. Aguarde a aprovação para realizar operações.';
      case 'SUSPENDED':
        return 'Sua conta foi suspensa temporariamente. Entre em contato com o suporte para mais informações.';
      case 'TERMINATED':
        return 'Sua conta foi encerrada. Entre em contato com o suporte se acredita que isso foi um erro.';
      default:
        return 'Algumas operações estão temporariamente indisponíveis. Complete a verificação de identidade para liberar todas as funcionalidades.';
    }
  };

  return (
    <div className={cn('rounded-lg border p-4', styles.bg, className)}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5">
          {getStatusIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={cn('font-semibold text-lg', styles.title)}>
            {getStatusTitle()}
          </h3>
          <p className={cn('mt-1 text-sm', styles.text)}>
            {getInstructions()}
          </p>

          {/* Lista de operações bloqueadas */}
          {blockedOperations.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className={cn('text-xs font-medium uppercase tracking-wide', styles.text)}>
                Operações bloqueadas:
              </p>
              <ul className="space-y-1">
                {blockedOperations.map((blocked, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <XCircle className={cn('w-4 h-4 flex-shrink-0 mt-0.5', styles.icon)} />
                    <span className={styles.text}>
                      <strong>{blocked.operation}</strong> - {blocked.reason}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Indicador de conta ativa com restrições parciais */}
          {contractStatus === 'ACTIVE' && (
            <div className="mt-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-300">
                Contrato ativo - algumas operações disponíveis
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContractStatusBanner;
